'use client';

import { Building2, Image as ImageIcon, Landmark, LoaderCircle, Save, Upload } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

type CompanyProfile = Readonly<{
  id: string;
  version: number;
  name: string;
  address: string | null;
  contactName: string | null;
  tel: string | null;
  email: string | null;
  defaultLanguage: 'en-US' | 'zh-CN';
  numberingPrefix: string | null;
}>;

type BankAccountView = Readonly<{
  accountId: string;
  versionId: string;
  version: number;
  bankName: string;
  accountName: string;
  maskedAccountNumber: string;
  currency: string;
  status: 'ACTIVE' | 'INACTIVE';
}>;

type ApiFailure = Readonly<{ message?: string }>;

export function CompanySettings() {
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [accounts, setAccounts] = useState<readonly BankAccountView[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<'company' | 'bank' | 'logo' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const companyResponse = await fetch('/api/company', { cache: 'no-store' });
      if (!companyResponse.ok) throw new Error(await responseMessage(companyResponse));
      const companyPayload = (await companyResponse.json()) as {
        profile: CompanyProfile | null;
        logoAssetId: string | null;
        canEdit: boolean;
      };
      setCompany(companyPayload.profile);
      setCanEdit(companyPayload.canEdit);
      if (companyPayload.logoAssetId) {
        const previewResponse = await fetch(`/api/logo?assetId=${encodeURIComponent(companyPayload.logoAssetId)}`, { cache: 'no-store' });
        if (previewResponse.ok) {
          const preview = (await previewResponse.json()) as { url: string };
          setLogoUrl(preview.url);
        } else {
          setLogoUrl(null);
        }
      } else {
        setLogoUrl(null);
      }

      const bankResponse = await fetch('/api/bank-accounts', { cache: 'no-store' });
      if (bankResponse.ok) {
        const bankPayload = (await bankResponse.json()) as { accounts: readonly BankAccountView[]; canEdit: boolean };
        setAccounts(bankPayload.accounts);
        setCanEdit(companyPayload.canEdit && bankPayload.canEdit);
      } else if (bankResponse.status !== 403) {
        throw new Error(await responseMessage(bankResponse));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '配置读取失败。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  async function submitCompany(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending('company');
    setError(null);
    setMessage(null);
    const form = new FormData(event.currentTarget);
    try {
      const response = await jsonMutation('/api/company', {
        name: form.get('name'),
        address: form.get('address'),
        contactName: form.get('contactName'),
        tel: form.get('tel'),
        email: form.get('email'),
        defaultLanguage: form.get('defaultLanguage'),
        numberingPrefix: form.get('numberingPrefix'),
      }, 'PATCH');
      if (!response.ok) throw new Error(await responseMessage(response));
      setMessage('公司资料版本已保存。');
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '公司资料保存失败。');
    } finally {
      setPending(null);
    }
  }

  async function submitBank(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setPending('bank');
    setError(null);
    setMessage(null);
    const form = new FormData(formElement);
    const accountId = String(form.get('accountId') ?? '');
    try {
      const response = await jsonMutation('/api/bank-accounts', {
        accountId: accountId || undefined,
        bankName: form.get('bankName'),
        bankAddress: form.get('bankAddress'),
        accountName: form.get('accountName'),
        accountNumber: form.get('accountNumber'),
        swiftCode: form.get('swiftCode'),
        iban: form.get('iban'),
        currency: form.get('currency'),
        active: form.get('active') === 'on',
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      formElement.reset();
      setMessage('银行账户版本已保存。');
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '银行账户保存失败。');
    } finally {
      setPending(null);
    }
  }

  async function submitLogo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('logo');
    if (!(file instanceof File) || file.size === 0) {
      setError('请选择 PNG 或 JPEG Logo。');
      return;
    }
    setPending('logo');
    setError(null);
    setMessage(null);
    try {
      const response = await fetch('/api/logo', {
        method: 'POST',
        headers: { 'content-type': file.type, 'idempotency-key': crypto.randomUUID() },
        body: file,
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = (await response.json()) as { status: 'AVAILABLE' | 'QUARANTINED' };
      formElement.reset();
      setMessage(result.status === 'AVAILABLE' ? 'Logo 已提交并完成安全处理。' : '扫描服务不可用，Logo 已隔离且不会显示。');
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Logo 上传失败。');
    } finally {
      setPending(null);
    }
  }

  if (loading) {
    return <div className="settings-loading"><LoaderCircle className="spin" size={20} /> 正在读取配置</div>;
  }

  return (
    <div className="settings-stack">
      {(message || error) && (
        <p className={error ? 'form-error' : 'form-message'} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </p>
      )}

      <section className="settings-section" aria-labelledby="company-settings-title">
        <div className="settings-section-heading">
          <Building2 size={20} aria-hidden="true" />
          <div><h2 id="company-settings-title">公司主体</h2><span>{company ? `版本 ${company.version}` : '未配置'}</span></div>
        </div>
        <form className="settings-form" onSubmit={submitCompany}>
          <fieldset disabled={!canEdit || pending !== null}>
            <div className="settings-fields settings-fields-two">
              <label>公司名称<input name="name" defaultValue={company?.name ?? ''} required maxLength={200} /></label>
              <label>联系人<input name="contactName" defaultValue={company?.contactName ?? ''} maxLength={200} /></label>
              <label className="settings-field-wide">地址<textarea name="address" defaultValue={company?.address ?? ''} rows={3} maxLength={1000} /></label>
              <label>电话<input name="tel" defaultValue={company?.tel ?? ''} maxLength={100} /></label>
              <label>邮箱<input name="email" type="email" defaultValue={company?.email ?? ''} maxLength={320} /></label>
              <label>默认语言<select name="defaultLanguage" defaultValue={company?.defaultLanguage ?? 'en-US'}><option value="en-US">English</option><option value="zh-CN">中文</option></select></label>
              <label>编号前缀<input name="numberingPrefix" defaultValue={company?.numberingPrefix ?? ''} maxLength={20} /></label>
            </div>
            {canEdit && <button className="primary-button" type="submit"><Save size={17} />{pending === 'company' ? '保存中' : '保存新版本'}</button>}
          </fieldset>
        </form>
      </section>

      <section className="settings-section" aria-labelledby="bank-settings-title">
        <div className="settings-section-heading">
          <Landmark size={20} aria-hidden="true" />
          <div><h2 id="bank-settings-title">银行账户</h2><span>{accounts.length} 个已确认账户</span></div>
        </div>
        {accounts.length > 0 ? (
          <div className="bank-list" role="list">
            {accounts.map((account) => (
              <div className="bank-row" role="listitem" key={account.accountId}>
                <div><strong>{account.bankName}</strong><span>{account.accountName}</span></div>
                <div><span>{account.maskedAccountNumber}</span><span>{account.currency} · V{account.version}</span></div>
              </div>
            ))}
          </div>
        ) : <p className="settings-empty">暂无银行账户</p>}

        {canEdit && (
          <form className="settings-form settings-form-separated" onSubmit={submitBank}>
            <fieldset disabled={pending !== null}>
              <div className="settings-fields settings-fields-two">
                <label>保存到<select name="accountId" defaultValue=""><option value="">新账户</option>{accounts.map((account) => <option key={account.accountId} value={account.accountId}>{account.bankName} · {account.maskedAccountNumber}</option>)}</select></label>
                <label>币种<select name="currency" defaultValue="USD"><option>USD</option><option>EUR</option><option>GBP</option><option>HKD</option><option>CNY</option></select></label>
                <label>银行名称<input name="bankName" required maxLength={200} /></label>
                <label>账户名称<input name="accountName" required maxLength={200} /></label>
                <label>账号<input name="accountNumber" required maxLength={100} autoComplete="off" /></label>
                <label>SWIFT<input name="swiftCode" maxLength={50} autoComplete="off" /></label>
                <label>IBAN<input name="iban" maxLength={100} autoComplete="off" /></label>
                <label>银行地址<input name="bankAddress" maxLength={1000} /></label>
              </div>
              <label className="settings-check"><input name="active" type="checkbox" defaultChecked />启用账户</label>
              <button className="primary-button" type="submit"><Save size={17} />{pending === 'bank' ? '保存中' : '保存银行版本'}</button>
            </fieldset>
          </form>
        )}
      </section>

      <section className="settings-section" aria-labelledby="logo-settings-title">
        <div className="settings-section-heading">
          <ImageIcon size={20} aria-hidden="true" />
          <div><h2 id="logo-settings-title">公司 Logo</h2><span>PNG / JPEG · 2 MB</span></div>
        </div>
        {logoUrl && (
          <figure className="logo-preview">
            {/* A short-lived signed URL can target any configured S3-compatible host. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={logoUrl} alt="当前公司 Logo" />
          </figure>
        )}
        {canEdit ? (
          <form className="logo-form" onSubmit={submitLogo}>
            <input name="logo" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" disabled={pending !== null} />
            <button className="primary-button" type="submit" disabled={pending !== null}><Upload size={17} />{pending === 'logo' ? '上传中' : '上传 Logo'}</button>
          </form>
        ) : <p className="settings-empty">当前角色仅可查看公司配置。</p>}
      </section>
    </div>
  );
}

async function jsonMutation(url: string, body: unknown, method: 'PATCH' | 'POST' = 'POST'): Promise<Response> {
  return fetch(url, {
    method,
    headers: { 'content-type': 'application/json', 'idempotency-key': crypto.randomUUID() },
    body: JSON.stringify(body),
  });
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiFailure;
    return body.message ?? `请求失败（${response.status}）`;
  } catch {
    return `请求失败（${response.status}）`;
  }
}
